/* eslint-disable no-mixed-operators, react/function-component-definition,
react/require-default-props, max-len */
import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import SvgFileZoomPan from '@complat/react-svg-file-zoom-pan';
import ReactQuill from 'react-quill';

import {
  Accordion, AccordionSummary, Divider, Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { withStyles } from '@mui/styles';

import Format from '../../helpers/format';
import { updateDSCMetaData } from '../../actions/meta';

const ELECTRON_MASS = 0.000548579909065;

const styles = () => ({
  chip: {
    margin: '1px 0 1px 0',
  },
  panel: {
    backgroundColor: '#eee',
    display: 'table-row',
  },
  panelSummary: {
    backgroundColor: '#eee',
    height: 32,
  },
  subSectionHeader: {
    backgroundColor: '#eee',
    height: 32,
    lineHeight: '32px',
    paddingLeft: 10,
    textAlign: 'left',
    fontWeight: 'bold',
    fontSize: '0.8rem',
    fontFamily: 'Helvetica',
    borderTop: '1px solid #dcdcdc',
    color: 'rgba(0, 0, 0, 0.87)',
  },
  // No height cap: the enclosing `panels` is the one vertical scroll container. Width is
  // another matter - `panels` sets `overflowX: hidden`, so anything wider than the column
  // would be cut off; scroll it sideways here instead.
  panelDetail: {
    backgroundColor: '#fff',
    overflowX: 'auto',
  },
  table: {
    width: 'auto',
  },
  rowRoot: {
    border: '1px solid #eee',
    height: 36,
    lineHeight: '36px',
    overflow: 'hidden',
    paddingLeft: 24,
    textAlign: 'left',
  },
  rowOdd: {
    backgroundColor: '#fff',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  rowEven: {
    backgroundColor: '#fafafa',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  rowOddSim: {
    backgroundColor: '#fff',
    height: 'auto',
    minHeight: 36,
    lineHeight: '24px',
    overflow: 'visible',
    overflowWrap: 'anywhere',
  },
  simPlaceholder: {
    color: 'rgba(0, 0, 0, 0.54)',
    fontStyle: 'italic',
  },
  tHead: {
    fontWeight: 'bold',
    float: 'left',
    fontSize: '0.8rem',
    fontFamily: 'Helvetica',
  },
  tTxt: {
    fontSize: '0.8rem',
    fontFamily: 'Helvetica',
    marginRight: 3,
  },
  quill: {
    fontSize: '0.8rem',
    fontFamily: 'Helvetica',
    textAlign: 'left',
  },
  quillContainer: {
    margin: '10px 10px',
    backgroundColor: '#fff',
    '& .ql-container': {
      border: 'none',
    },
    '& .ql-editor': {
      minHeight: '60px',
    },
    '& .ql-editor.ql-blank::before': {
      fontStyle: 'normal',
      color: 'rgba(0, 0, 0, 0.54)',
    },
  },
});

const simTitle = () => (
  'Simulated signals from NMRshiftDB'
);

const simContent = (nmrSimPeaks) => {
  if (!Array.isArray(nmrSimPeaks) || nmrSimPeaks.length === 0) return '';
  return [...nmrSimPeaks].sort((a, b) => a - b).join(', ');
};

const simPlaceholder = () => (
  'No simulated signals available.'
);

const normalizeQuillValue = (val) => {
  if (!val) return '';
  if (val === '<p><br></p>' || val === '<p></p>') return '';
  return val;
};

const formatMsExactMass = (exactMass) => {
  const neutralMass = parseFloat(exactMass);
  if (Number.isNaN(neutralMass)) return null;

  return (neutralMass - ELECTRON_MASS).toFixed(6);
};

const chemSubStyle = {
  fontSize: '0.85em',
  position: 'relative',
  top: '0.24em',
  lineHeight: 1,
};

const renderReadableSubscript = (txt = '') => {
  if (typeof txt !== 'string') return txt;
  const regex = /([A-Za-z])(\d+)/g;
  if (!regex.test(txt)) return txt;
  regex.lastIndex = 0;
  const parts = [];
  let cursor = 0;
  let match = regex.exec(txt);
  while (match) {
    const [raw, prefix, digits] = match;
    const at = match.index;
    if (at > cursor) parts.push(txt.slice(cursor, at));
    parts.push(prefix);
    parts.push(
      <span key={`${at}-${digits}`} style={chemSubStyle}>{digits}</span>,
    );
    cursor = at + raw.length;
    match = regex.exec(txt);
  }
  if (cursor < txt.length) parts.push(txt.slice(cursor));
  return parts;
};

const handleDescriptionChanged = (content, delta, source, editor, onDescriptionChanged) => {
  if (!onDescriptionChanged) return;
  onDescriptionChanged(normalizeQuillValue(content), delta, source, editor);
};

const aucValue = (integration, hplcMsSt) => {
  const values = [];
  const stackIntegration = integration?.stack;
  if (Array.isArray(stackIntegration)) {
    let sumVal = 0.0;
    stackIntegration.forEach((inte) => {
      if (inte.absoluteArea) {
        sumVal += inte.absoluteArea;
      }
    });
    sumVal = sumVal.toFixed(2);
    stackIntegration.forEach((inte) => {
      if (inte.absoluteArea) {
        const areaVal = inte.absoluteArea.toFixed(2);
        const percent = (areaVal * 100 / sumVal).toFixed(2);
        const valStr = areaVal + " (" + percent + "%)"; // eslint-disable-line
        values.push(valStr);
      }
    });
  }

  const spectraList = hplcMsSt?.uvvis?.spectraList || [];
  const listWaveLength = hplcMsSt?.uvvis?.listWaveLength || [];

  spectraList.forEach((spectrum, idx) => {
    const wavelength = listWaveLength[idx];
    const integrations = spectrum?.integrations || [];

    if (integrations.length > 0) {
      const sumArea = integrations.reduce(
        (sum, integ) => sum + (integ.absoluteArea ?? integ.area ?? 0),
        0,
      );

      const integrationStrings = integrations.map((integ) => {
        const rawArea = integ.absoluteArea ?? integ.area ?? 0;
        const areaVal = rawArea.toFixed(2);
        const percent = sumArea > 0 ? ((rawArea * 100) / sumArea).toFixed(2) : '0.00';
        return `${areaVal} (${percent}%)`;
      });

      values.push(`[${wavelength} nm]: ${integrationStrings.join(', ')}`);
    }
  });

  return values.join('\n');
};

const SECData = ({
  classes, layout, detector, secData,
}) => {
  if (Format.isSECLayout(layout) && secData) {
    const {
      d, mn, mp, mw,
    } = secData;
    return (
      <div>
        <div className={classNames(classes.rowRoot, classes.rowOdd)}>
          <span className={classNames(classes.tTxt, classes.tHead, 'txt-sv-panel-txt')}>Detector: </span>
          <span className={classNames(classes.tTxt, 'txt-sv-panel-txt')}>{detector}</span>
        </div>
        <div className={classNames(classes.rowRoot, classes.rowEven)}>
          <span className={classNames(classes.tTxt, classes.tHead, 'txt-sv-panel-txt')}>D: </span>
          <span className={classNames(classes.tTxt, 'txt-sv-panel-txt')}>{d}</span>
        </div>
        <div className={classNames(classes.rowRoot, classes.rowOdd)}>
          <span className={classNames(classes.tTxt, classes.tHead, 'txt-sv-panel-txt')}>MN: </span>
          <span className={classNames(classes.tTxt, 'txt-sv-panel-txt')}>{mn}</span>
        </div>
        <div className={classNames(classes.rowRoot, classes.rowEven)}>
          <span className={classNames(classes.tTxt, classes.tHead, 'txt-sv-panel-txt')}>MP: </span>
          <span className={classNames(classes.tTxt, 'txt-sv-panel-txt')}>{mp}</span>
        </div>
        <div className={classNames(classes.rowRoot, classes.rowOdd)}>
          <span className={classNames(classes.tTxt, classes.tHead, 'txt-sv-panel-txt')}>MW: </span>
          <span className={classNames(classes.tTxt, 'txt-sv-panel-txt')}>{mw}</span>
        </div>
      </div>
    );
  }
  return null;
};

SECData.propTypes = {
  classes: PropTypes.object.isRequired,
  layout: PropTypes.string.isRequired,
  detector: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
  ]),
  secData: PropTypes.object,
};

SECData.defaultProps = {
  detector: '',
};

const DSCData = ({
  classes, layout, dscMetaData, updateAction,
}) => {
  if (Format.isDSCLayout(layout) && dscMetaData !== undefined) {
    const {
      meltingPoint, tg,
    } = dscMetaData;

    const onChange = (e) => {
      const { name, value } = e.target;
      const dataToUpdate = { meltingPoint, tg };
      dataToUpdate[name] = value;
      updateAction(dataToUpdate);
    };

    return (
      <div>
        <div className={classNames(classes.rowRoot, classes.rowOdd)}>
          <span className={classNames(classes.tTxt, classes.tHead, 'txt-sv-panel-txt')}>Melting Point: </span>
          <input type="text" name="meltingPoint" className={classNames(classes.tTxt, 'txt-sv-panel-txt')} value={meltingPoint} onChange={onChange} />
        </div>
        <div className={classNames(classes.rowRoot, classes.rowEven)}>
          <span className={classNames(classes.tTxt, classes.tHead, 'txt-sv-panel-txt')}>TG: </span>
          <input type="text" name="tg" className={classNames(classes.tTxt, 'txt-sv-panel-txt')} value={tg} onChange={onChange} />
        </div>
      </div>
    );
  }
  return null;
};

DSCData.propTypes = {
  classes: PropTypes.object.isRequired,
  layout: PropTypes.string.isRequired,
  dscMetaData: PropTypes.object,
  updateAction: PropTypes.func.isRequired,
};

const InfoPanel = ({
  classes, expand, feature, integration, editorOnly, molSvg, descriptions,
  layoutSt, simulationSt, shiftSt, curveSt, exactMass,
  onExpand, canChangeDescription, onDescriptionChanged, detectorSt,
  metaSt, updateDSCMetaDataAct, hplcMsSt, entities,
}) => {
  if (!feature) return null;
  const msExactMass = Format.isMsLayout(layoutSt) && exactMass
    ? formatMsExactMass(exactMass)
    : null;
  const {
    title, observeFrequency, solventName, secData,
  } = feature;
  const { dscMetaData } = metaSt;
  const { curveIdx } = curveSt;
  const { curves = [] } = detectorSt || {};
  const currentEntity = Array.isArray(entities) ? entities[curveIdx] : null;
  const entityTitle = currentEntity?.entity?.title
    || currentEntity?.title
    || currentEntity?.spectra?.[0]?.title
    || currentEntity?.entity?.spectra?.[0]?.title
    || '';
  const displayTitle = title || entityTitle;

  const getSelectedDetectorForCurve = (_detectorSt, targetCurveIdx) => {
    const targetCurve = curves.find((curve) => curve.curveIdx === targetCurveIdx);

    return targetCurve?.selectedDetector?.name || '';
  };

  let selectedDetector = getSelectedDetectorForCurve(detectorSt, curveIdx);

  // default detector from jcamp
  if (!selectedDetector && feature.detector) {
    selectedDetector = feature.detector;
  }

  const { shifts } = shiftSt;
  const selectedShift = shifts[curveIdx];
  let showSolvName = solventName;
  if (selectedShift !== undefined) {
    const shiftName = selectedShift.ref.name;
    showSolvName = shiftName === '- - -' ? solventName : shiftName;
  }

  let originStack = null;
  if (integration) {
    originStack = integration.originStack;  // eslint-disable-line
  }
  const simulatedSignals = simContent(simulationSt.nmrSimPeaks);

  return (
    <Accordion
      data-testid="PanelInfo"
      expanded={expand}
      onChange={onExpand}
      disableGutters
      sx={{
        '&.MuiAccordion-root.Mui-expanded': { margin: 0 },
        '&:before': { display: 'none' },
      }}
      TransitionProps={{ unmountOnExit: true }} // increase Accordion performance
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        className={classNames(classes.panelSummary)}
      >
        <Typography className="txt-panel-header">
          <span className={classNames(classes.txtBadge, 'txt-sv-panel-title')}>
            Info
          </span>
        </Typography>
      </AccordionSummary>
      <Divider />
      <div className={classNames(classes.panelDetail)}>
        <div className={classNames(classes.rowRoot, classes.rowOdd)}>
          <span className={classNames(classes.tTxt, classes.tHead, 'txt-sv-panel-txt')}>Title : </span>
          <span className={classNames(classes.tTxt, 'txt-sv-panel-txt')}>{ displayTitle }</span>
        </div>
        {
          Format.isNmrLayout(layoutSt)
            ? (
              <div className={classNames(classes.rowRoot, classes.rowEven)}>
                <span className={classNames(classes.tTxt, classes.tHead, 'txt-sv-panel-txt')}>Freq : </span>
                <span className={classNames(classes.tTxt, 'txt-sv-panel-txt')}>{ `${parseInt(observeFrequency, 10)} MHz` || ' - ' }</span>
              </div>
            )
            : null
        }
        {
          Format.isNmrLayout(layoutSt)
            ? (
              <div className={classNames(classes.rowRoot, classes.rowOdd)}>
                <span className={classNames(classes.tTxt, classes.tHead, 'txt-sv-panel-txt')}>Solv : </span>
                <span className={classNames(classes.tTxt, 'txt-sv-panel-txt')}>{renderReadableSubscript(showSolvName)}</span>
              </div>
            )
            : null
        }
        {
          msExactMass
            ? (
              <div className={classNames(classes.rowRoot, classes.rowOdd)}>
                <span className={classNames(classes.tTxt, classes.tHead, 'txt-sv-panel-txt')}>Exact mass (M+): </span>
                <span className={classNames(classes.tTxt, 'txt-sv-panel-txt')}>{`${msExactMass} g/mol`}</span>
              </div>
            )
            : null
        }
        <SECData classes={classes} layout={layoutSt} detector={selectedDetector} secData={secData} />
        {
          !molSvg
            ? null
            : (
              <SvgFileZoomPan
                svg={molSvg}
                duration={300}
                resize
              />
            )
        }
        {
          (Format.isHplcUvVisLayout(layoutSt)) ? (
            <div className={classNames(classes.rowRoot, classes.rowOddSim)}>
              <span className={classNames(classes.tTxt, classes.tHead, 'txt-sv-panel-txt')}>
                Area under curve (AUC):
              </span>
              <br />
              <span className={classNames(classes.tTxt, classes.tTxtSim, 'txt-sv-panel-txt')}>
                {aucValue(integration)}
              </span>
            </div>
          ) : null
        }
        <DSCData classes={classes} layout={layoutSt} dscMetaData={dscMetaData} updateAction={updateDSCMetaDataAct} />
        {
          !Format.isCyclicVoltaLayout(layoutSt)
            ? (
              <>
                <div className={classes.subSectionHeader}>
                  Content
                </div>
                <div className={classes.quillContainer}>
                  <ReactQuill
                    className={classNames(classes.quill, 'card-sv-quill')}
                    value={normalizeQuillValue(descriptions)}
                    placeholder={canChangeDescription ? 'Peaks will be written here...' : undefined}
                    readOnly={!canChangeDescription}
                    modules={{ toolbar: false }}
                    onChange={(content, delta, source, editor) => handleDescriptionChanged(content, delta, source, editor, onDescriptionChanged)}
                  />
                </div>
              </>
            ) : null
        }
        {
          !editorOnly && Format.isNmrLayout(layoutSt)
            ? (
              <>
                <div className={classes.subSectionHeader}>
                  { simTitle() }
                </div>
                <div className={classNames(classes.rowRoot, classes.rowOddSim)}>
                  <span
                    className={classNames(
                      classes.tTxt,
                      classes.tTxtSim,
                      !simulatedSignals && classes.simPlaceholder,
                      'txt-sv-panel-txt',
                    )}
                  >
                    { simulatedSignals || simPlaceholder() }
                  </span>
                </div>
              </>
            )
            : null
        }
      </div>
      {
        Format.isLCMsLayout(layoutSt) ? (
          <div className={classNames(classes.rowRoot, classes.rowOddSim)}>
            <span className={classNames(classes.tTxt, classes.tHead, 'txt-sv-panel-txt')}>
              Area under curve (AUC):
            </span>
            <br />
            <div
              className={classNames(classes.tTxt, classes.tTxtSim, 'txt-sv-panel-txt')}
              style={{
                maxHeight: '80px',
                overflowY: 'auto',
                overflowX: 'hidden',
                wordBreak: 'break-word',
                marginBottom: '100px',
              }}
            >
              {aucValue(integration, hplcMsSt)
                .split('\n')
                .map((line, idx) => (
                  <div key={idx}>{line}</div>
                ))}
            </div>
          </div>
        ) : null
      }
    </Accordion>
  );
};

const mapStateToProps = (state, props) => ( // eslint-disable-line
  {
    layoutSt: state.layout,
    simulationSt: state.simulation,
    shiftSt: state.shift,
    curveSt: state.curve,
    detectorSt: state.detector,
    metaSt: state.meta,
    hplcMsSt: state.hplcMs,
  }
);

const mapDispatchToProps = (dispatch) => (
  bindActionCreators({
    updateDSCMetaDataAct: updateDSCMetaData,
  }, dispatch)
);

InfoPanel.propTypes = {
  classes: PropTypes.object.isRequired,
  expand: PropTypes.bool.isRequired,
  feature: PropTypes.object,
  integration: PropTypes.object,
  editorOnly: PropTypes.bool.isRequired,
  molSvg: PropTypes.string.isRequired,
  descriptions: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.array,
  ]).isRequired,
  layoutSt: PropTypes.string.isRequired,
  simulationSt: PropTypes.object.isRequired,
  shiftSt: PropTypes.object.isRequired,
  curveSt: PropTypes.object.isRequired,
  onExpand: PropTypes.func,
  canChangeDescription: PropTypes.bool.isRequired,
  onDescriptionChanged: PropTypes.func,
  exactMass: PropTypes.string,
  detectorSt: PropTypes.object.isRequired,
  metaSt: PropTypes.object.isRequired,
  updateDSCMetaDataAct: PropTypes.func.isRequired,
  hplcMsSt: PropTypes.object.isRequired,
  entities: PropTypes.array,
};

export default connect( // eslint-disable-line
  mapStateToProps, mapDispatchToProps,
)(withStyles(styles)(InfoPanel)); // eslint-disable-line
